import axios from 'axios';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

console.log('[API] baseURL =', API_URL);

// Create axios instance
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add token
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  console.log('[API]', config.method?.toUpperCase(), config.baseURL + config.url);
  return config;
});

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => {
    console.log('[API] OK', response.status, response.config.url);
    return response;
  },
  (error) => {
    console.error('[API] ERR', error.response?.status, error.config?.url, error.response?.data);
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (email, password) => api.post('/auth/login', { email, password }),
  register: (data) => api.post('/auth/register', data),
  me: () => api.get('/auth/me'),
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
  resetPassword: (token, new_password) => api.post('/auth/reset-password', { token, new_password }),
};

// Users API
export const usersAPI = {
  getAll: () => api.get('/users'),
  update: (id, data) => api.put(`/users/${id}`, data),
  updatePermissions: (id, permissions) => api.put(`/users/${id}/permissions`, { permissions }),
  updatePreferences: (data) => api.put('/users/me/preferences', data),
  changeMyPassword: (data) => api.put('/users/me/password', data),
  changePassword: (id, data) => api.put(`/users/${id}/password`, data),
  delete: (id) => api.delete(`/users/${id}`),
};

// Clients API
export const clientsAPI = {
  getAll: () => api.get('/clients'),
  getOne: (id) => api.get(`/clients/${id}`),
  create: (data) => api.post('/clients', data),
  update: (id, data) => api.put(`/clients/${id}`, data),
  delete: (id) => api.delete(`/clients/${id}`),
};

// Contracts API
export const contractsAPI = {
  getAll: (clientId) => api.get('/contracts', { params: { client_id: clientId } }),
  getOne: (id) => api.get(`/contracts/${id}`),
  create: (data) => api.post('/contracts', data),
  update: (id, data) => api.put(`/contracts/${id}`, data),
  delete: (id) => api.delete(`/contracts/${id}`),
  syncDeadlines: (id) => api.post(`/contracts/${id}/sync-deadlines`),
};

// Quotes API
export const quotesAPI = {
  getAll: (clientId) => api.get('/quotes', { params: { client_id: clientId } }),
  create: (data) => api.post('/quotes', data),
  update: (id, data) => api.put(`/quotes/${id}`, data),
  updateStatus: (id, stato) => api.put(`/quotes/${id}/status`, null, { params: { stato } }),
  delete: (id) => api.delete(`/quotes/${id}`),
};

// Invoices API
export const invoicesAPI = {
  getAll: (clientId) => api.get('/invoices', { params: { client_id: clientId } }),
  create: (data) => api.post('/invoices', data),
  update: (id, data) => api.put(`/invoices/${id}`, data),
  updateStatus: (id, stato) => api.put(`/invoices/${id}/status`, null, { params: { stato } }),
  delete: (id) => api.delete(`/invoices/${id}`),
};

// Deadlines API
export const deadlinesAPI = {
  getAll: () => api.get('/deadlines'),
  create: (data) => api.post('/deadlines', data),
  update: (id, data) => api.put(`/deadlines/${id}`, data),
  complete: (id) => api.put(`/deadlines/${id}/complete`),
  reopen: (id) => api.put(`/deadlines/${id}/reopen`),
  delete: (id) => api.delete(`/deadlines/${id}`),
};

// Services API
export const servicesAPI = {
  getAll: () => api.get('/services'),
  create: (data) => api.post('/services', data),
  update: (id, data) => api.put(`/services/${id}`, data),
  delete: (id) => api.delete(`/services/${id}`),
};

// Projects API
export const projectsAPI = {
  getAll: (clientId) => api.get('/projects', { params: { client_id: clientId } }),
  getOne: (id) => api.get(`/projects/${id}`),
  create: (data) => api.post('/projects', data),
  update: (id, data) => api.put(`/projects/${id}`, data),
  updateStatus: (id, stato) => api.put(`/projects/${id}/status`, null, { params: { stato } }),
  delete: (id) => api.delete(`/projects/${id}`),
  getAttachments: (id) => api.get(`/projects/${id}/attachments`),
  downloadAttachment: (projectId, attachId) => api.get(`/projects/${projectId}/attachments/${attachId}`),
  addAttachment: (id, data) => api.post(`/projects/${id}/attachments`, data),
  deleteAttachment: (projectId, attachId) => api.delete(`/projects/${projectId}/attachments/${attachId}`),
  getNotes: (id) => api.get(`/projects/${id}/notes`),
  addNote: (id, data) => api.post(`/projects/${id}/notes`, data),
  deleteNote: (projectId, noteId) => api.delete(`/projects/${projectId}/notes/${noteId}`),
  getTasks: (id) => api.get(`/projects/${id}/tasks`),
  createTask: (id, data) => api.post(`/projects/${id}/tasks`, data),
  updateTask: (projectId, taskId, data) => api.put(`/projects/${projectId}/tasks/${taskId}`, data),
  deleteTask: (projectId, taskId) => api.delete(`/projects/${projectId}/tasks/${taskId}`),
};

// Time Entries API
export const timeEntriesAPI = {
  getAll: (projectId) => api.get('/time-entries', { params: { project_id: projectId } }),
  create: (data) => api.post('/time-entries', data),
  update: (id, data) => api.put(`/time-entries/${id}`, data),
  delete: (id) => api.delete(`/time-entries/${id}`),
};

// Notifications API
export const notificationsAPI = {
  getAll: () => api.get('/notifications'),
  markRead: (id) => api.put(`/notifications/${id}/read`),
  markAllRead: () => api.put('/notifications/read-all'),
};

// Settings API
export const settingsAPI = {
  get: () => api.get('/settings'),
  update: (data) => api.put('/settings', data),
};

// Dashboard API
export const dashboardAPI = {
  getStats: () => api.get('/dashboard/stats'),
  getProjectsInProgress: () => api.get('/dashboard/projects-in-progress'),
  getRevenueChart: () => api.get('/dashboard/revenue-chart'),
  getServicesDistribution: () => api.get('/dashboard/services-distribution'),
};

// Domains API
export const dominiAPI = {
  getAll: (clientId) => api.get('/domains', { params: clientId ? { client_id: clientId } : {} }),
  create: (data) => api.post('/domains', data),
  update: (id, data) => api.put(`/domains/${id}`, data),
  delete: (id) => api.delete(`/domains/${id}`),
};

// Push Notifications API
export const pushAPI = {
  getVapidKey: () => api.get('/push/vapid-public-key'),
  subscribe: (subscription) => api.post('/push/subscribe', subscription),
  unsubscribe: (subscription) => api.delete('/push/unsubscribe', { data: subscription }),
};

// Calendar API
export const calendarAPI = {
  getEvents: (params) => api.get('/calendar/events', { params }),
  createEvent: (data) => api.post('/calendar/events', data),
  updateEvent: (id, data) => api.put(`/calendar/events/${id}`, data),
  deleteEvent: (id) => api.delete(`/calendar/events/${id}`),
};

// Chat API
export const chatAPI = {
  getConversations: (params) => api.get('/chat/conversations', { params }),
  createConversation: (data) => api.post('/chat/conversations', data),
  updateStatus: (id, stato) => api.put(`/chat/conversations/${id}/status`, null, { params: { stato } }),
  deleteConversation: (id) => api.delete(`/chat/conversations/${id}`),
  getMessages: (convId) => api.get(`/chat/conversations/${convId}/messages`),
  sendMessage: (convId, data) => api.post(`/chat/conversations/${convId}/messages`, data),
  resolveMessage: (msgId) => api.put(`/chat/messages/${msgId}/resolve`),
  updateMessage: (msgId, data) => api.put(`/chat/messages/${msgId}`, data),
  getFocus: () => api.get('/chat/focus'),
  search: (q) => api.get('/chat/search', { params: { q } }),
  // Reactions
  getReactions: (msgId) => api.get(`/chat/messages/${msgId}/reactions`),
  addReaction: (msgId, emoji, waMsgId = '', senderName = '') =>
    api.post(`/chat/messages/${msgId}/reactions`, null, { params: { emoji, wa_msg_id: waMsgId, sender_name: senderName } }),
  deleteReaction: (msgId, reactionId) => api.delete(`/chat/messages/${msgId}/reactions/${reactionId}`),
};

// WhatsApp API
export const whatsappAPI = {
  getBridgeStatus: () => api.get('/whatsapp/bridge-status'),
  getQR:          () => api.get('/whatsapp/qr'),
  logout:         () => api.post('/whatsapp/logout'),
  getRecentSenders: () => api.get('/whatsapp/recent-senders'),
  getGroups:        () => api.get('/whatsapp/groups'),
  getMappings:    () => api.get('/whatsapp/mappings'),
  createMapping:  (data) => api.post('/whatsapp/mappings', data),
  updateMapping:  (id, data) => api.put(`/whatsapp/mappings/${id}`, data),
  deleteMapping:  (id) => api.delete(`/whatsapp/mappings/${id}`),
};

export default api;
